<template>
	<main>
		<LoadingIcon v-if="isLoading" alignment="center" />
		<form class="details-form" v-if="!isLoading">
			<h3 class="heading">Basic Information</h3>
			<fieldset :disabled="mainProfileIsInternalUser">
				<div class="basic-information-container">
					<div>
						<FormInputs
							ref="firstName"
							label="First Name"
							name="first-name"
							required
							:maxLength="30"
							type="text"
							v-model="$v.contact.firstName.$model"
							:error="
								$v.contact.firstName.$dirty &&
								(isNoFirstName || formValidationError)
							"
							shouldFocus
						/>
						<div
							v-if="$v.contact.firstName.$dirty"
							data-test="error-message-div"
						>
							<div
								v-if="isNoFirstName || formValidationError"
								class="error"
							>
								First name is required.
							</div>
						</div>
					</div>
					<div>
						<FormInputs
							ref="lastName"
							label="Last Name"
							name="last-name"
							required
							:maxLength="80"
							type="text"
							v-model="$v.contact.lastName.$model"
							:error="
								$v.contact.lastName.$dirty &&
								(isNoLastName || formValidationError)
							"
						/>
						<div
							v-if="$v.contact.lastName.$dirty"
							data-test="error-message-div"
						>
							<div
								v-if="isNoLastName || formValidationError"
								class="error"
							>
								Last name is required.
							</div>
						</div>
					</div>
					<div>
						<FormInputs
							ref="email"
							label="Email Address"
							name="email"
							required
							:maxLength="80"
							type="text"
							v-model="$v.contact.email.$model"
							:error="
								$v.contact.email.$dirty &&
								(isInvalidEmail || isNoEmail)
							"
						/>
						<div
							v-if="$v.contact.email.$dirty"
							data-test="error-message-div"
						>
							<div v-if="isInvalidEmail" class="error">
								Invalid Email
							</div>
							<div v-if="isNoEmail" class="error">
								Email is required.
							</div>
						</div>
					</div>
					<div>
						<label class="birthday-label">Birthday</label>
						<Datepicker
							name="birthday"
							placeholder="Select Date"
							v-model="contact.birthday"
							inputClass="birthday-date-picker"
							calendarClass="birthday-calendar"
							:fullMonthName="true"
							format="MMMM dd"
							maximumView="month"
						/>
					</div>
					<FormInputs
						label="Phone Number"
						name="phone-number"
						type="text"
						placeholder="555-555-5555"
						:pattern="'\d{3}[\-]\d{3}[\-]\d{4}'"
						:mask="[
							/[1-9]/,
							/\d/,
							/\d/,
							'-',
							/\d/,
							/\d/,
							/\d/,
							'-',
							/\d/,
							/\d/,
							/\d/,
							/\d/,
						]"
						v-model="contact.phones[0].phoneNumber"
					/>
					<FormInputs
						label="Phone Ext."
						name="phone-extention"
						type="text"
						:mask="[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/]"
						v-model="contact.phones[0].phoneExt"
					/>
					<FormDropdowns
						label="Contact Type"
						name="contact-type"
						v-model="contact.type.id"
						:options="contactTypeOptions"
					/>
					<FormInputs
						v-if="isBusiness"
						label="Business Name"
						name="business-name"
						v-model="contact.businessName"
					/>
					<FormInputs
						label="Credit Score"
						name="credit-score"
						:maxLength="3"
						v-model="contact.ficoScore"
					/>
					<FormInputs
						label="Lead Source"
						name="lead-source"
						:disabled="true"
						v-model="contact.source.leadSourceDesc"
					/>
				</div>
				<h3 class="primary-address heading">
					{{ isBusiness ? 'Business Address' : 'Primary Address' }}
				</h3>
				<div class="primary-address-container">
					<FormInputs
						label="Address Line 1"
						name="primary-address-line-1"
						:maxLength="50"
						v-model="contact.primaryAddress.addressLine1"
					/>
					<FormInputs
						label="Address Line 2"
						name="primary-address-line-2"
						:maxLength="50"
						v-model="contact.primaryAddress.addressLine2"
					/>
					<FormInputs
						label="City"
						name="primary-address-city"
						:maxLength="30"
						v-model="contact.primaryAddress.city"
					/>
					<FormDropdowns
						label="State"
						name="primary-address-state"
						v-model="contact.primaryAddress.state"
						:options="states"
					/>
					<div>
						<FormInputs
							ref="primaryZipCode"
							label="Zip Code"
							name="primary-address-zip-code"
							type="text"
							:mask="[/\d/, /\d/, /\d/, /\d/, /\d/]"
							v-model="$v.contact.primaryAddress.zipCode.$model"
							:error="
								$v.contact.primaryAddress.zipCode.$dirty &&
								(isInvalidPrimaryZipCode || formValidationError)
							"
						/>
						<div
							v-if="
								isInvalidPrimaryZipCode &&
								$v.contact.primaryAddress.zipCode.$dirty
							"
							class="error"
							data-test="error-message-div"
						>
							Must be 5 digits
						</div>
					</div>
				</div>
				<h3 class="mailing-address heading">Mailing Address</h3>
				<div class="same-as-primary">
					<CheckBox
						id="same-as-primary-checkbox"
						class="same-as-primary-checkbox"
						@onClick="onCheckBoxClick"
						:checked="contact.sameAsPrimaryAddress"
						ariaLabel="Mailing address same as primary address"
					/>
					<label
						for="same-as-primary-checkbox"
						class="same-as-primary-label"
						>Same as
						{{ isBusiness ? 'business' : 'primary' }} address</label
					>
				</div>
				<div
					v-if="!contact.sameAsPrimaryAddress"
					class="mailing-address-container"
				>
					<FormInputs
						label="Address Line 1"
						name="mailing-address-line-1"
						:maxLength="50"
						v-model="contact.mailingAddress.addressLine1"
					/>
					<FormInputs
						label="Address Line 2"
						name="mailing-address-line-2"
						:maxLength="50"
						v-model="contact.mailingAddress.addressLine2"
					/>
					<FormInputs
						label="City"
						name="mailing-address-city"
						:maxLength="30"
						v-model="contact.mailingAddress.city"
					/>
					<FormDropdowns
						label="State"
						name="mailing-address-state"
						v-model="contact.mailingAddress.state"
						:options="states"
					/>
					<div>
						<FormInputs
							ref="mailingZipCode"
							label="Zip Code"
							name="mailing-address-zip-code"
							type="text"
							:mask="[/\d/, /\d/, /\d/, /\d/, /\d/]"
							v-model="$v.contact.mailingAddress.zipCode.$model"
							:error="
								$v.contact.mailingAddress.zipCode.$dirty &&
								(isInvalidMailingZipCode || formValidationError)
							"
						/>
						<div
							v-if="
								isInvalidMailingZipCode &&
								$v.contact.mailingAddress.zipCode.$dirty
							"
							class="error"
							data-test="error-message-div"
						>
							Must be 5 digits
						</div>
					</div>
				</div>
			</fieldset>
			<div class="button-container">
				<BaseButton
					class="save-button"
					color="blue"
					:disabled="mainProfileIsInternalUser || isInvalidForm"
					@onClick="handleSave"
					ariaLabel="save"
				>
					Save
				</BaseButton>
				<BaseButton
					class="cancel-button"
					color="white"
					@onClick="handleCancel"
					ariaLabel="cancel"
				>
					Cancel
				</BaseButton>
			</div>
		</form>
	</main>
</template>

<script>
import { isEmpty, cloneDeep } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import { required, email, minLength } from 'vuelidate/lib/validators';

import BaseButton from '@/components/global/BaseButton.vue';
import CheckBox from '../CheckBox.vue';
import FormDropdowns from '@/components/global/FormDropdowns.vue';
import FormInputs from '@/components/global/FormInputs.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';

export default {
	name: 'ContactDetails',
	components: {
		BaseButton,
		CheckBox,
		Datepicker,
		FormDropdowns,
		FormInputs,
		LoadingIcon,
	},
	props: {
		contactDetails: Object,
	},
	data() {
		return {
			isLoading: false,
			formValidationError: false,
			contact: {
				firstName: null,
				lastName: null,
				email: null,
				birthday: null,
				businessName: null,
				phones: [
					{
						phoneExt: null,
						phoneNumber: null,
						type: {
							phoneTypeId: null,
						},
					},
				],
				type: {
					desc: 'Borrower',
					id: 1,
				},
				primaryAddress: {
					addressLine1: null,
					addressLine2: null,
					city: null,
					state: null,
					zipCode: null,
					county: null,
				},
				sameAsPrimaryAddress: true,
				mailingAddress: {
					addressLine1: null,
					addressLine2: null,
					city: null,
					state: null,
					zipCode: null,
					county: null,
				},
				source: {
					leadSourceId: 0,
					leadSourceDesc: '',
				},
			},
		};
	},
	validations() {
		return {
			contact: {
				firstName: {
					required,
				},
				lastName: {
					required,
				},
				email: {
					required,
					email,
				},
				primaryAddress: {
					zipCode: {
						minLength: minLength(5),
					},
				},
				mailingAddress: {
					zipCode: {
						minLength: minLength(5),
					},
				},
			},
		};
	},
	computed: {
		...mapGetters(['contactTypeOptions']),
		...mapState({
			states: (state) => state.ccContacts.leads.states,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
		isInvalidForm() {
			return this.$v.contact.$invalid;
		},
		isNoFirstName() {
			return this.$v.contact.firstName.$invalid;
		},
		isNoLastName() {
			return this.$v.contact.lastName.$invalid;
		},
		isInvalidEmail() {
			return !this.$v.contact.email.email;
		},
		isNoEmail() {
			return !this.$v.contact.email.required;
		},
		isInvalidPrimaryZipCode() {
			return this.$v.contact.primaryAddress.zipCode.$invalid;
		},
		isInvalidMailingZipCode() {
			return this.$v.contact.mailingAddress.zipCode.$invalid;
		},
		isBusiness() {
			let businessTypes = [3, 5, 7];
			return businessTypes.includes(this.contact.type.id);
		},
	},
	async created() {
		if (isEmpty(this.states)) {
			this.isLoading = true;
			await this.getAllStates();
			this.isLoading = false;
		}
		this.formatContact();
	},
	methods: {
		...mapActions(['getAllStates', 'editSingleContact']),
		formatContact() {
			if (!isEmpty(this.contactDetails)) {
				if (isEmpty(this.contactDetails.phones)) {
					this.contact = {
						...this.contactDetails,
						...{ phones: this.contact.phones },
					};
				} else {
					this.contact = { ...this.contactDetails };
				}
				this.contact = {
					...this.contact,
					...{ birthday: this.getBirthday(this.contact.birthday) },
				};
			}
		},
		getBirthday(date) {
			// if no birthday was saved, the api returns 01-01-0001, so set it to null
			return new Date(date).getFullYear() === 1 ? null : new Date(date);
		},
		onCheckBoxClick(selected) {
			this.contact.sameAsPrimaryAddress = selected;
			if (selected) {
				this.contact.mailingAddress = {
					...this.contact.primaryAddress,
				};
			}
		},
		formatPhones(phone) {
			let newPhone = phone;
			if (!newPhone[0].phoneNumber) {
				newPhone = [];
			} else {
				// if (phone.phoneExt) {
				// 	phone.type.phoneTypeId = 2;
				// } else {
				newPhone[0].type.phoneTypeId = 1;
				// }
				newPhone[0].phoneNumber = newPhone[0].phoneNumber.replace(
					/-/g,
					'',
				);
			}
			return newPhone;
		},
		// eslint-disable-next-line consistent-return
		async saveContact() {
			let newContact = cloneDeep(this.contact);

			newContact.phones = this.formatPhones(cloneDeep(newContact.phones));
			const phoneNumber =
				newContact.phones.length !== 0
					? newContact.phones[0].phoneNumber
					: null;
			const isNew = !newContact.leadId ? true : false;
			if (!isNew) {
				this.editSingleContact({
					id: newContact.leadId,
					firstName: newContact.firstName,
					lastName: newContact.lastName,
					email: newContact.email,
					phone: phoneNumber,
					type: newContact.type.desc,
					owner: newContact.owner,
				});
			}
			if (!this.isBusiness && newContact.businessName !== null) {
				newContact.businessName = '';
			}
			this.$emit('onContactSave', {
				contact: newContact,
				newContact: isNew,
			});
		},
		handleSave() {
			this.formValidationError = this.isInvalidForm;
			if (this.formValidationError) {
				this.scrollErrorIntoView();
			} else {
				this.saveContact();
			}
		},
		handleCancel() {
			this.$emit('onCancel');
		},
		scrollErrorIntoView() {
			let errorRef;

			if (this.isNoFirstName) {
				errorRef = this.$refs.firstName.$el;
			} else if (this.isNoLastName) {
				errorRef = this.$refs.lastName.$el;
			} else if (this.isInvalidEmail || this.isNoEmail) {
				errorRef = this.$refs.email.$el;
			} else if (this.isInvalidMailingZipCode) {
				errorRef = this.$refs.mailingZipCode.$el;
			}
			setTimeout(() => {
				errorRef.scrollIntoView({ behavior: 'smooth' });
			}, 50);
		},
	},
};
</script>

<style lang="scss" scoped>
.details-form {
	@include rem('padding', 20px);

	fieldset {
		display: block;
		margin: 0;
		padding: 0;
		border: none;
	}

	.birthday-label {
		color: $b360-color--gray__darkest;
		font-weight: 500;
		@include rem('margin-bottom', 16px);
	}

	/deep/ .vdp-datepicker {
		.vdp-datepicker__calendar {
			.cell {
				&.selected {
					background: $brand-blue;
					color: $white;
				}
			}
		}
		.birthday-date-picker {
			width: 100%;
			border: 1px solid $border-color-light;
			color: $b360-color--gray__darkest;
			@include rem('border-radius', 6px);
			@include rem('padding', 12px);
			@include rem('margin-top', 14px);
			@include rem('font-size', 16px);
			cursor: pointer;
			outline: none;
		}
		.birthday-calendar {
			@include rem('right', 0);
			@include rem('border-radius', 6px);
			width: 100%;
		}
	}

	.save-button,
	.cancel-button,
	.heading {
		@include rem('margin-bottom', 20px);
	}

	.primary-address,
	.mailing-address,
	.button-container {
		@include rem('padding-top', 15px);
		@include rem('margin-top', 20px);
		border-top: 1px solid $border-color-light;
	}

	@media only screen and (min-width: 769px) {
		.form-inputs {
			/deep/ .b360-form-input {
				width: 270px;
			}
		}

		.save-button,
		.cancel-button {
			width: 270px;
		}

		.basic-information-container,
		.primary-address-container,
		.mailing-address-container,
		.button-container {
			display: grid;
			grid-template-columns: auto auto;
			@include rem('grid-gap', 20px);
		}
	}
}

.spinner {
	@include rem('padding', 25px);
	display: flex;
	justify-content: center;
	align-items: center;
}

.error {
	color: $error;
	/* font-style: italic; */
}

.same-as-primary {
	display: flex;
	align-items: center;
	@include rem('margin-bottom', 20px);

	.same-as-primary-label {
		@include rem('margin-left', 26px);
		@include rem('margin-top', 1px);
	}
}
</style>
